/**
 * =============================================================================
 * Records Sync Health Page (issue #22, supersedes #20)
 * =============================================================================
 *
 * GOVERNANCE: admins have no access to patient data. This page shows only an
 * AGGREGATED sync-health view of patient exercise records:
 *
 * - table: one row per institution x app with counts by upload status
 *   (pending / uploaded / failed), the oldest pending timestamp and the
 *   total stored size (when the BE can provide it)
 * - filters: institution (platform admins), app
 * - NO patient identifiers, NO record IDs, NO object paths, NO downloads
 *
 * The detailed per-record view (paths, existence checks, downloads) lives in
 * the CLINIC portal, scoped to the institution and audited.
 *
 * Access: system_admin, rehapiano_admin (all institutions),
 * institution_admin (own institution only).
 */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  appsApi,
  institutionsApi,
  recordsApi,
  SyncHealthItem,
} from '../services/api'
import { useAuth } from '../auth/AuthProvider'

// =============================================================================
// Helpers
// =============================================================================

const formatDateTime = (value?: string | null): string => {
  if (!value) return '-'
  return new Date(value).toLocaleDateString('sk-SK', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const formatBytes = (value?: number | null): string => {
  if (value === null || value === undefined) return '—'
  if (value < 1024) return `${value} B`
  const units = ['KB', 'MB', 'GB', 'TB']
  let v = value / 1024
  let i = 0
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024
    i += 1
  }
  return `${v.toFixed(1)} ${units[i]}`
}

/** Pending records older than this are highlighted as a sync problem. */
const STALE_PENDING_MS = 24 * 60 * 60 * 1000

const isStalePending = (item: SyncHealthItem): boolean =>
  item.pending_count > 0 &&
  !!item.oldest_pending_at &&
  Date.now() - new Date(item.oldest_pending_at).getTime() > STALE_PENDING_MS

// =============================================================================
// Count Badge
// =============================================================================

const CountBadge: React.FC<{
  value: number
  kind: 'pending' | 'uploaded' | 'failed'
}> = ({ value, kind }) => {
  if (value === 0) {
    return <span className="text-gray-300">0</span>
  }
  const className =
    kind === 'uploaded'
      ? 'badge badge-active'
      : kind === 'pending'
        ? 'badge badge-warning'
        : 'badge bg-red-100 text-red-800'
  return <span className={className}>{value}</span>
}

// =============================================================================
// Main Page Component
// =============================================================================

const RecordsPage: React.FC = () => {
  const { hasRole } = useAuth()
  const isPlatformAdmin = hasRole('system_admin') || hasRole('rehapiano_admin')

  // Filters
  const [institutionFilter, setInstitutionFilter] = useState('')
  const [appFilter, setAppFilter] = useState('')

  // Institutions for the filter dropdown (platform admins only)
  const { data: institutions } = useQuery({
    queryKey: ['institutions'],
    queryFn: () => institutionsApi.list(),
    enabled: isPlatformAdmin,
  })

  // Apps for the filter dropdown
  const { data: apps } = useQuery({
    queryKey: ['apps'],
    queryFn: () => appsApi.list(),
  })

  const params = {
    ...(institutionFilter ? { institution_id: institutionFilter } : {}),
    ...(appFilter ? { app_id: appFilter } : {}),
  }

  const { data, isLoading, error } = useQuery({
    queryKey: ['records-sync-health', params],
    queryFn: () => recordsApi.syncHealth(params),
  })

  const items = data?.data.items || []
  const totalRecords = data?.data.total_records || 0
  const totalPending = items.reduce((sum, i) => sum + i.pending_count, 0)
  const totalFailed = items.reduce((sum, i) => sum + i.failed_count, 0)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sync Health</h1>
          <p className="text-gray-500 text-sm mt-1">
            Aggregated upload state of exercise records per institution and
            app. Contains no patient data — the detailed record view lives in
            the Clinic portal.
          </p>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="card">
          <div className="text-sm text-gray-500">Total records</div>
          <div className="text-2xl font-bold text-gray-900">{totalRecords}</div>
        </div>
        <div className="card">
          <div className="text-sm text-gray-500">Pending</div>
          <div className={`text-2xl font-bold ${totalPending > 0 ? 'text-yellow-600' : 'text-gray-900'}`}>
            {totalPending}
          </div>
        </div>
        <div className="card">
          <div className="text-sm text-gray-500">Failed</div>
          <div className={`text-2xl font-bold ${totalFailed > 0 ? 'text-red-600' : 'text-gray-900'}`}>
            {totalFailed}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {isPlatformAdmin && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="records-filter-institution">
                Institution
              </label>
              <select
                id="records-filter-institution"
                className="input"
                value={institutionFilter}
                onChange={(e) => setInstitutionFilter(e.target.value)}
              >
                <option value="">All institutions</option>
                {(institutions?.data || []).map((inst) => (
                  <option key={inst.id} value={inst.id}>
                    {inst.institution_name}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="records-filter-app">
              App
            </label>
            <select
              id="records-filter-app"
              className="input"
              value={appFilter}
              onChange={(e) => setAppFilter(e.target.value)}
            >
              <option value="">All apps</option>
              {(apps?.data || []).map((app) => (
                <option key={app.id} value={app.id}>
                  {app.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-left bg-gray-50 border-b border-gray-200">
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">Institution</th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">App</th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase text-right">Pending</th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase text-right">Uploaded</th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase text-right">Failed</th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase text-right">Total</th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">Oldest pending</th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase text-right" title="Total stored size (not yet tracked by the platform)">Size</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {isLoading && (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                    Loading sync health...
                  </td>
                </tr>
              )}
              {!!error && !isLoading && (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-red-500">
                    Failed to load sync health
                  </td>
                </tr>
              )}
              {!isLoading && !error && items.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                    No records yet. Rows appear here once devices sync
                    exercise sessions.
                  </td>
                </tr>
              )}
              {items.map((item) => {
                const stale = isStalePending(item)
                return (
                  <tr
                    key={`${item.institution_id || 'none'}-${item.app_id || 'none'}`}
                    className="hover:bg-gray-50"
                  >
                    <td className="px-4 py-3 text-sm font-medium">
                      {item.institution_name || <span className="text-gray-400">Unassigned</span>}
                    </td>
                    <td className="px-4 py-3 text-sm">{item.app_name || '-'}</td>
                    <td className="px-4 py-3 text-sm text-right">
                      <CountBadge value={item.pending_count} kind="pending" />
                    </td>
                    <td className="px-4 py-3 text-sm text-right">
                      <CountBadge value={item.uploaded_count} kind="uploaded" />
                    </td>
                    <td className="px-4 py-3 text-sm text-right">
                      <CountBadge value={item.failed_count} kind="failed" />
                    </td>
                    <td className="px-4 py-3 text-sm text-right font-medium">{item.total_count}</td>
                    <td className="px-4 py-3 text-sm whitespace-nowrap">
                      <span
                        className={stale ? 'text-yellow-700 font-medium' : undefined}
                        title={stale ? 'Pending for more than 24 hours — check the device sync' : undefined}
                      >
                        {formatDateTime(item.oldest_pending_at)}
                        {stale && ' ⚠'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-gray-500">
                      {formatBytes(item.total_size_bytes)}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export default RecordsPage
